import { JobbinInputGrey } from "../../components/jobbinInputGrey"
import { JobbinPrimaryButton } from "../../components/primaryButton"
import { ProfileDetails } from "../../components/profileDetails"
import { UpdateProfileSettingsRequest } from "./UpdateProfileSettingsRequest"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  changeProfilePicturePresigned,
  presignUpload,
  getSettings,
  updateProfileSettings,
  promoteEmployer,
  getPromotePricing,
} from "../../services/employer-service"
import { SettingsResponse } from "./model"
import { LoadingComponent } from "../../components/loading"
import { useToast } from "../../context/toast"
import { useCredits } from "../../context/credits"
import PromoteDurationModal from "../../components/promoteDurationModal"

export const SettingsPage = () => {
  const navigate = useNavigate()
  const { notify } = useToast()
  const { balance: creditBalance, refresh: refreshCredits } = useCredits()
  const [settings, setSettings] = useState<SettingsResponse>(null)
  const [location, setLocation] = useState("")
  const [businessName, setBusinessName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)
  const [promoted, setPromoted] = useState(false)
  const [promotedUntil, setPromotedUntil] = useState<string | undefined>(undefined)
  const [pricing, setPricing] = useState<{ days: number; cost: number }[]>([])
  const [showPromoteDurationModal, setShowPromoteDurationModal] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const daysRemaining = promotedUntil
    ? Math.max(0, Math.ceil((new Date(promotedUntil).getTime() - Date.now()) / 86_400_000))
    : 0
  const formattedUntil = promotedUntil
    ? new Date(promotedUntil).toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" })
    : ""
  const isPromoActive = promoted && daysRemaining > 0
  const recommendedDays = pricing.length ? pricing[Math.floor(pricing.length / 2)].days : null
  const fromCredits = pricing.length ? Math.min(...pricing.map((t) => t.cost)) : null
  const companyInitials = (businessName || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("")

  const logout = () => {
    localStorage.removeItem("jwtToken")
    navigate("/")
  }

  const updateProfile = async () => {
    const request: UpdateProfileSettingsRequest = {
      location,
      businessName,
      phoneNumber: phoneNumber || undefined,
      notificationsEnabled,
    }
    const response = await updateProfileSettings(request)
    if (response.ok) {
      await getProfileSettings()
      notify("Schimbările au fost aplicate cu succes!", "success")
    } else notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
  }

  useEffect(() => {
    getProfileSettings()
    getPromotePricing()
      .then((tiers: { days: number; cost: number }[]) => setPricing(tiers ?? []))
      .catch(() => setPricing([]))
  }, [])

  const getProfileSettings = async () => {
    setLoading(true)
    const profileSettings = (await getSettings()) as SettingsResponse
    setSettings(profileSettings)
    setBusinessName(profileSettings.businessName)
    setLocation(profileSettings.location)
    setPhoneNumber(profileSettings.phoneNumber ?? "")
    setNotificationsEnabled(`${profileSettings.notificationsEnabled}` === "true")
    setPromoted(profileSettings.promoted === true)
    setPromotedUntil(profileSettings.promotedUntil)
    setLoading(false)
  }

  const openPromoteModal = () => {
    setShowPromoteDurationModal(true)
  }

  const onPromoteConfirm = async (days: number) => {
    if (days == null) return
    setPromoting(true)
    try {
      const res = await promoteEmployer(days)
      if (res.status === 422 || res.status === 400) {
        setShowPromoteDurationModal(false)
        navigate("/credits")
        return
      }
      if (res.ok) {
        const wasActive = isPromoActive
        setShowPromoteDurationModal(false)
        await getProfileSettings()
        refreshCredits()
        notify(wasActive ? "Promovarea a fost prelungită!" : "Compania ta este acum promovată!", "success")
      } else {
        notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
      }
    } finally {
      setPromoting(false)
    }
  }

  const onImageSelected = async (img: File) => {
    setLoading(true)
    try {
      const extension = img.name.split(".").pop() || ""
      const presign = await presignUpload(extension)
      await fetch(presign.uploadUrl, {
        method: "PUT",
        body: img,
        headers: { "Content-Type": img.type || "image/" + extension },
      })
      const response = await changeProfilePicturePresigned(presign.fileName, presign.publicUrl)
      if (response.ok) {
        await getProfileSettings()
      } else {
        notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
      }
    } catch {
      notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div><LoadingComponent /></div>
  }

  return (
      <div id="settings-page" className="w-full px-4 py-4 sm:px-6 sm:py-6">

        {/* Promovează compania */}
        <div className="overflow-hidden rounded-[20px] border border-figmaPrimary/15 bg-gradient-to-br from-figmaPrimary/[0.07] via-white to-white">
          <div className="p-5 sm:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-stretch lg:justify-between">
              {/* Stanga: preview story + texte + beneficii */}
              <div className="flex items-start gap-4 sm:gap-5 min-w-0">
                {/* Preview live + schimba logo (clickabil) */}
                <div className="flex shrink-0 flex-col items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="group relative rounded-full"
                    aria-label="Schimbă logo-ul companiei"
                  >
                    <div className={`rounded-full p-[2.5px] ${isPromoActive ? "bg-gradient-to-tr from-figmaPrimary to-figmaContrast" : "bg-figmaLightGray"}`}>
                      <div className="rounded-full bg-white p-[2px]">
                        {settings?.profilePictureUrl ? (
                          <img
                            src={settings.profilePictureUrl}
                            alt={businessName}
                            className="h-16 w-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-figmaPrimary/10 text-figmaPrimary font-bold text-lg">
                            {companyInitials || "•"}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-figmaPrimary text-white ring-2 ring-white transition-transform group-hover:scale-110">
                      <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5">
                        <path d="M4 8.5h3L8.5 6h7L17 8.5h3v11H4v-11z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        <circle cx="12" cy="13.5" r="2.8" stroke="currentColor" strokeWidth="1.8" />
                      </svg>
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) onImageSelected(file)
                      e.target.value = ""
                    }}
                  />
                  <span className="text-[10px] font-medium text-figmaPrimary">Schimbă logo</span>
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-figmaPrimary/10 text-figmaPrimary">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                        <path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" />
                      </svg>
                    </span>
                    <p className="text-figmaBlack font-bold text-[16px] sm:text-[17px]">Promovează compania</p>
                    {isPromoActive && (
                      <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-bold text-sky-600 ring-1 ring-sky-200">
                        Activ
                      </span>
                    )}
                  </div>

                  {isPromoActive ? (
                    <p className="text-figmaDarkGray text-[13px] mt-1.5">
                      Activ până la <span className="font-semibold text-figmaBlack">{formattedUntil}</span>
                      {" · "}
                      <span className="font-semibold text-figmaPrimary">
                        {daysRemaining} {daysRemaining === 1 ? "zi rămasă" : "zile rămase"}
                      </span>
                    </p>
                  ) : (
                    <p className="text-figmaDarkGray text-[13px] mt-1.5 max-w-md">
                      Ieși în evidență în fața candidaților, peste tot în aplicație.
                    </p>
                  )}

                  <ul className="mt-3 space-y-1.5">
                    {[
                      "Apari în secțiunea „Companii promovate”",
                      "Joburile tale urcă sus în listă",
                      "Logo evidențiat cu ramă colorată",
                    ].map((benefit) => (
                      <li key={benefit} className="flex items-center gap-2 text-[13px] text-figmaBlack">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-figmaPrimary/10">
                          <svg viewBox="0 0 24 24" fill="none" className="h-3 w-3 text-figmaPrimary">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Dreapta: pret + buton */}
              <div className="flex flex-col justify-between gap-3 lg:items-end lg:text-right">
                {!isPromoActive && fromCredits != null && (
                  <p className="text-[13px] text-figmaDarkGray order-2 lg:order-1">
                    De la <span className="font-bold text-figmaBlack">{fromCredits} credite</span>
                  </p>
                )}
                <button
                  type="button"
                  onClick={openPromoteModal}
                  className="order-1 lg:order-2 w-full lg:w-auto rounded-xl bg-figmaPrimary px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-figmaPrimary/30 transition-all hover:opacity-90 hover:shadow-md"
                >
                  {isPromoActive ? "Prelungește promovarea" : "Promovează compania"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Setari */}
        <div className="mt-6">
          <ProfileDetails onToggle={setNotificationsEnabled} />
        </div>

        {/* Actualizeaza profil + Statistici */}
        <div id="more-details" className="mt-6">
          <div id="about-details" className="w-full">
            <div id="input-name" className="mt-5">
              <JobbinInputGrey
                  type="name"
                  label="Nume locație"
                  name="business-name"
                  onChange={(e) => setBusinessName((e.target as HTMLInputElement).value)}
                  value={businessName}
              />
            </div>

            <div id="input-location" className="mt-3">
              <JobbinInputGrey
                  type="location"
                  iconStart="location-icon.svg"
                  label="Adresa"
                  name="locatie"
                  onChange={(e) => setLocation((e.target as HTMLInputElement).value)}
                  value={location}
              />
            </div>

            <div id="input-phone" className="mt-3">
              <JobbinInputGrey
                  type="tel"
                  label="Număr de telefon"
                  name="phone"
                  onChange={(e) => setPhoneNumber((e.target as HTMLInputElement).value)}
                  value={phoneNumber}
              />
            </div>

            <div className="mt-4 flex justify-end">
              <div className="w-full sm:w-[220px]">
                <JobbinPrimaryButton onClickAction={updateProfile} text="Aplică schimbări" />
              </div>
            </div>

            <div className="mt-5 rounded-[16px] bg-white px-4 py-3 sm:px-6 sm:py-4" style={{ border: "1px solid #e5e7eb" }}>
              <div className="flex items-center justify-between gap-4 border-b-2 border-figmaWhite py-4">
                <p className="text-figmaBlack font-semibold">Job-uri publicate</p>
                <p className="text-figmaDarkGray text-right">{settings?.nrOfPostedGigs}</p>
              </div>
              <div className="flex items-center justify-between gap-4 py-4">
                <p className="text-figmaBlack font-semibold">Candidați</p>
                <p className="text-figmaDarkGray text-right">{settings?.nrOfApplicants}</p>
              </div>
            </div>

            <PromoteDurationModal
              isOpen={showPromoteDurationModal}
              title={isPromoActive ? "Prelungește promovarea" : "Promovează compania"}
              subtitle={isPromoActive
                ? "Zilele alese se adaugă peste cele rămase."
                : "Ieși în evidență în fața candidaților, peste tot în aplicație."}
              tiers={pricing}
              balance={creditBalance}
              recommendedDays={recommendedDays ?? undefined}
              loading={promoting}
              confirmLabel={isPromoActive ? "Prelungește" : "Promovează"}
              onConfirm={onPromoteConfirm}
              onCancel={() => setShowPromoteDurationModal(false)}
            />
          </div>
        </div>

      </div>
  )
}
